import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FCM } from '@capacitor-community/fcm';
import { Capacitor } from '@capacitor/core';
import { ActionPerformed, Channel, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { ToastController } from '@ionic/angular/lazy';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { StorageKeys } from 'src/app/app.constants';
import { ServerUrl } from '../../services/server-url.service';
import { StorageService } from '../../services/storage.service';
import { LoggingService } from '../../shared/logging.service';

@Injectable({
  providedIn: 'root',
})
export class FirebaseCloudMessagingService {
  readonly #http = inject(HttpClient);
  readonly #storage = inject(StorageService);
  readonly #toastController = inject(ToastController);
  readonly #serverUrl = inject(ServerUrl);
  readonly #log = inject(LoggingService);

  readonly #prefix = '/me';

  readonly #CHANNEL_ID_RESPONSABLE = 'responsable_alert';
  readonly #CHANNEL_ID_AGENT = 'agent_alert';

  readonly #notificationTapped$ = new Subject<PushNotificationSchema>();
  /**
   * Don't use this observable, as it's not typed. Use the one from NotificationInteractionService instead
   */
  readonly notificationTapped$ = this.#notificationTapped$.asObservable();

  readonly #notificationReceived$ = new Subject<PushNotificationSchema>();
  /**
   * Don't use this observable, as it's not typed. Use the one from NotificationInteractionService instead
   */
  readonly notificationReceived$ = this.#notificationReceived$.asObservable();

  async register(isResponsable: boolean, displayToast: boolean = true): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const wantNotification = await this.#storage.get(StorageKeys.WANT_NOTIFICATIONS);
    if (wantNotification === false) {
      this.#log.info('user explicitly refuse notification');
      return;
    }
    await this.#storage.set(StorageKeys.WANT_NOTIFICATIONS, true); // permanently save the user registration choice

    let pushNotificationsPermStatus = await PushNotifications.checkPermissions();

    if (pushNotificationsPermStatus.receive === 'prompt') {
      pushNotificationsPermStatus = await PushNotifications.requestPermissions();
    }

    if (pushNotificationsPermStatus.receive !== 'granted') {
      await this.presentToast(
        "Veuillez autoriser l'accès aux notifications pour pouvoir utiliser toutes les fonctionnalités de l'application."
      );
    }

    await PushNotifications.removeAllListeners();

    const channel: Channel = {
      ...(isResponsable
        ? {
            id: 'responsable_alert', // required
            name: 'Alertes responsable', // required
            description: 'Notifications pour les responsables sécurité.',
          }
        : {
            id: 'agent_alert', // required
            name: 'Alertes agent', // required
            description: 'Notifications pour les agents sécurité.',
          }),
      importance: 5, // https://developer.android.com/reference/android/app/NotificationManager#IMPORTANCE_DEFAULT
      visibility: 1, // https://developer.android.com/reference/androidx/core/app/NotificationCompat#VISIBILITY_PRIVATE
      lights: true, // enable lights for notifications
      vibration: true, // enable vibration for notifications
      // Pas de `sound` : le plugin n'appelle alors pas `setSound()`, et le canal conserve le son
      // de notification par défaut du système. En fournir un imposerait un fichier de `res/raw`.
    };

    // Le canal doit exister AVANT la première notification : le backend l'attache à chaque message
    // (`AndroidNotification.setChannelId`), et Android retomberait sinon sur le canal de repli de
    // Firebase, en importance 3 — donc sans bannière ni son.
    if (Capacitor.getPlatform() === 'android') {
      await PushNotifications.createChannel(channel);
    }

    await PushNotifications.addListener('registration', async (token: Token) => {
      this.saveToken(token.value).subscribe(() => {
        if (displayToast) {
          this.presentToast('Les notifications sont maintenant activées.', () => this.disable(isResponsable, true).subscribe());
        }
      });
    });

    await PushNotifications.addListener('registrationError', (error: any) => {
      alert('Error on registration: ' + JSON.stringify(error));
    });

    await PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
      if (Capacitor.getPlatform() === 'android') {
        const all = (await PushNotifications.getDeliveredNotifications()).notifications.filter(n => n.group !== 'ranker_group');
        const toRemove = all.filter(n => parseInt(n.id, 10) < 1600000000);
        await PushNotifications.removeDeliveredNotifications({ notifications: toRemove });
      }
      // Le plugin push affiche lui-même la bannière au premier plan (`presentationOptions`,
      // `capacitor.config.ts`) sur le canal porté par le message. Replanifier une notification
      // locale ici en afficherait une seconde.
      this.#notificationReceived$.next(notification);
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (event: ActionPerformed) => {
      this.#notificationTapped$.next(event.notification);
    });

    // En dernier : `register()` déclenche l'événement `registration`, qui serait perdu si son
    // écouteur n'était pas encore posé — le jeton ne partirait alors jamais au serveur.
    await PushNotifications.register();

    try {
      await FCM.subscribeTo({ topic: isResponsable ? this.#CHANNEL_ID_RESPONSABLE : this.#CHANNEL_ID_AGENT });
      this.#log.info('subscribed to topic ...');
    } catch (e) {
      this.#log.warn('fail to subscribe');
    }
  }

  /**
   * Désactive les notifications
   * @param isResponsable (nécessaire au cas où on annule l'action via le toast)
   * @param displayToast
   */
  disable(isResponsable: boolean, displayToast: boolean = true) {
    return this.unregisterFromServer().pipe(
      catchError(() => of({})),
      switchMap(() => from(this.#storage.remove(StorageKeys.FCM_TOKEN))),
      // tap(() => from(this.storage.remove(StorageKeys.WANT_NOTIFICATIONS))),
      tap(() => {
        this.#log.warn('removeAllListeners()');
        if (Capacitor.isNativePlatform()) {
          PushNotifications.removeAllListeners();
        }
        if (displayToast) {
          this.presentToast('Les notifications sont maintenant désactivées.', () => this.enable(isResponsable));
        }
      })
    );
  }

  /**
   * Active les notifications
   * @param isResponsable
   * @param displayToast
   */
  enable(isResponsable: boolean, displayToast: boolean = true): Observable<void> {
    return from(this.#storage.set(StorageKeys.WANT_NOTIFICATIONS, true)).pipe(tap(from(this.register(isResponsable, displayToast))));
  }

  /**
   * Désactive les notifications et supprime les canaux de notification
   * @param isResponsable (nécessaire au cas où on annule l'action via le toast)
   */
  destroy(isResponsable: boolean): Observable<void> {
    return this.disable(isResponsable, false).pipe(
      tap(from(this.#storage.remove(StorageKeys.WANT_NOTIFICATIONS))),
      tap(from(PushNotifications.deleteChannel({ id: 'responsable_alert' }))),
      tap(from(PushNotifications.deleteChannel({ id: 'agent_alert' })))
    );
  }

  async isRegistered(): Promise<boolean> {
    return this.#storage.get(StorageKeys.FCM_TOKEN).then(v => v != null);
  }
  private saveToken(token: string): Observable<void> {
    return this.sendTokenToServer(token).pipe(switchMap(() => from(this.#storage.set(StorageKeys.FCM_TOKEN, token))));
  }

  private sendTokenToServer(token: string): Observable<void> {
    return this.#http.post<void>(this.#serverUrl.get() + this.#prefix + '/fcmtoken', token);
  }

  private unregisterFromServer(): Observable<void> {
    if (!this.#serverUrl.get()) {
      return of(void 0);
    }
    return this.#http.delete<void>(this.#serverUrl.get() + this.#prefix + '/fcmtoken');
  }

  private async presentToast(message: string, cancelAction?: () => void) {
    const buttons = cancelAction
      ? [
          {
            text: 'Annuler',
            handler: () => cancelAction(),
          },
        ]
      : [];

    const toast = await this.#toastController.create({
      message,
      duration: 3000,
      buttons,
    });
    toast.present();
  }
}
